import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, MailOpen, Building2 } from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import {
  OrganizationService,
  type PublicInvitation,
} from "@/services/organization.service";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AlertMessage } from "@/components/ui/alert-message";

export default function AcceptInvitationPage() {
  const { invitationId } = useParams<{ invitationId: string }>();
  const { user, isAuthenticated, isLoading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();

  const [invitation, setInvitation] = useState<PublicInvitation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      if (!invitationId) return;
      try {
        const data = await OrganizationService.getPublicInvitation(invitationId);
        setInvitation(data);
      } catch {
        setInvitation(null);
      } finally {
        setIsLoading(false);
      }
    };
    void load();
  }, [invitationId]);

  const handleAccept = async () => {
    if (!invitationId) return;
    setIsAccepting(true);
    setError("");
    try {
      await OrganizationService.acceptInvitation(invitationId);
      // Full reload re-runs the auth bootstrap so the fresh tenant (clientId)
      // and membership are picked up everywhere.
      window.location.href = "/";
    } catch (err: any) {
      if (err?.code === "EMAIL_MISMATCH") {
        setError(
          "This invitation was sent to a different email address. Sign out and sign in with the invited address."
        );
      } else if (err?.code === "USER_HAS_ORGANIZATION") {
        setError("Your account already belongs to an organization.");
      } else {
        setError(err?.message || "Failed to accept the invitation.");
      }
      setIsAccepting(false);
    }
  };

  const content = () => {
    if (isLoading || authLoading) {
      return (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (!invitation) {
      return (
        <div className="flex flex-col items-center gap-3 text-center">
          <Building2 className="h-10 w-10 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Invitation not found</h2>
          <p className="text-sm text-muted-foreground">
            This invitation link is invalid. Ask your organization admin to send a new one.
          </p>
        </div>
      );
    }

    if (invitation.status !== "pending") {
      return (
        <div className="flex flex-col items-center gap-3 text-center">
          <Building2 className="h-10 w-10 text-muted-foreground" />
          <h2 className="text-lg font-semibold">
            This invitation is {invitation.status}
          </h2>
          <p className="text-sm text-muted-foreground">
            Ask your organization admin to send a new invitation to join{" "}
            {invitation.organizationName}.
          </p>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center gap-4 text-center">
        <MailOpen className="h-10 w-10 text-purple-500" />
        <div className="space-y-1">
          <h2 className="text-xl font-semibold">
            Join {invitation.organizationName}
          </h2>
          <p className="text-sm text-muted-foreground">
            {invitation.inviterName} invited {invitation.email} to join as{" "}
            <span className="font-medium capitalize">{invitation.role ?? "member"}</span>.
          </p>
        </div>

        <AlertMessage message={error} type="error" />

        {isAuthenticated ? (
          <div className="flex flex-col items-center gap-3">
            <p className="text-xs text-muted-foreground">
              Signed in as <span className="font-medium">{user?.email}</span>
            </p>
            <div className="flex items-center gap-3">
              <Button
                onClick={handleAccept}
                disabled={isAccepting}
                className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
              >
                {isAccepting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Accept invitation
              </Button>
              <Button variant="outline" onClick={() => signOut()}>
                Use another account
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <p className="text-xs text-muted-foreground">
              Sign in or create an account with the invited email address to accept.
            </p>
            <div className="flex items-center gap-3">
              <Button
                className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
                onClick={() =>
                  navigate(`/signup?invitation=${invitationId}`, { replace: false })
                }
              >
                Create account
              </Button>
              <Button
                variant="outline"
                onClick={() =>
                  navigate(
                    `/signin?redirect=${encodeURIComponent(`/accept-invitation/${invitationId}`)}`
                  )
                }
              >
                Sign in
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-6">
      <Card className="w-full max-w-lg">
        <CardContent className="py-12">
          <div className="mb-8 flex justify-center">
            <div className="flex items-center gap-2">
              <img src="/logo.svg" alt="Youzu.ai" className="h-8 w-8" />
              <span className="bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-xl font-bold text-transparent">
                Youzu.ai
              </span>
            </div>
          </div>
          {content()}
          <p className="mt-8 text-center text-xs text-muted-foreground">
            Invitation expires {invitation ? new Date(invitation.expiresAt).toLocaleString() : ""}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
